"use client";

import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import {
  GetTokenBalanceParams,
  GetTokenBalanceResponse,
} from "@chipi-stack/types";
import { useChipiContext } from "../context";
import { ChipiApiError } from "@chipi-stack/shared";

type GetTokenBalanceInput = {
  params?: GetTokenBalanceParams;
  getBearerToken?: () => Promise<string | null | undefined>;
  queryOptions?: Omit<UseQueryOptions<GetTokenBalanceResponse, Error>, 'queryKey' | 'queryFn'>;
};

export function useGetTokenBalance(input?: GetTokenBalanceInput) {
  const { chipiSDK } = useChipiContext();
  const queryClient = useQueryClient();
  
  const query = useQuery<GetTokenBalanceResponse, Error>({
    queryKey: ["chain-token-balance", input?.params?.chainToken, input?.params?.chain, input?.params?.walletPublicKey, input?.params?.externalUserId],
    queryFn: async () => {
      if (!input || !input.params || !input.getBearerToken) throw new Error("Input is required");
      const bearerToken = await input.getBearerToken();
      if (!bearerToken) throw new Error("Bearer token is required");
      return chipiSDK.getTokenBalance(input.params, bearerToken);
    },
    enabled: Boolean(input?.params && input?.getBearerToken),
    ...input?.queryOptions,
    retry: (failureCount: number, error: Error) => {
      // Don't retry on API errors (errors with status codes like 404, 400, etc.)
      if (error instanceof ChipiApiError || (error as any)?.status) {
        return false;
      }
      // Retry on network errors (no status code) up to 3 times
      return failureCount < 3;
    },
  });

  const fetchTokenBalance = async (newInput: GetTokenBalanceInput) => {
    return queryClient.fetchQuery({
      queryKey: ["chain-token-balance", newInput?.params?.chainToken, newInput?.params?.chain, newInput?.params?.walletPublicKey, newInput?.params?.externalUserId],
      queryFn: async () => {
        if (!newInput || !newInput.getBearerToken || !newInput.params) throw new Error("Input is required");
        const bearerToken = await newInput.getBearerToken();
        if (!bearerToken) throw new Error("Bearer token is required");
        return chipiSDK.getTokenBalance(newInput.params, bearerToken);
      },

      retry: (failureCount: number, error: Error) => {
        // Don't retry on API errors (errors with status codes like 404, 400, etc.)
        if (error instanceof ChipiApiError || (error as any)?.status) {
          return false;
        }
        // Retry on network errors (no status code) up to 3 times
        return failureCount < 3;
      },
    });
    
  };

  return {
    ...query,
    fetchTokenBalance,
  };
}
